import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { t } from '../../utils/i18n';
import { formatDateTimeKST } from '../../utils/kstDateTime';
import UserDetailModal from '../../components/UserDetailModal/UserDetailModal';
import { useAuth } from '../../context/AuthContext';
import { useApiEnvironment } from '../../context/ApiEnvironmentContext';
import { getUserBanLogs } from '../../api/authAdmin';
import PaginatedTableCard from '../../components/PaginatedTableCard/PaginatedTableCard';

const BAN_HISTORY_TABLE_SKELETON_WIDTHS = ['w-12', 'w-14', 'w-24', 'w-16', 'w-14', 'w-32'];

const PAGE_SIZE = 10;

function ClickableUserId({ userId, onOpen }) {
  if (userId == null || userId === '') {
    return <span className="text-dark-text-secondary">-</span>;
  }
  const id = Number(userId);
  if (Number.isNaN(id) || id < 1) {
    return <span className="text-dark-text-secondary">{String(userId)}</span>;
  }
  return (
    <button
      type="button"
      className="text-primary font-medium underline-offset-2 hover:underline"
      onClick={() => onOpen(id)}
    >
      {id}
    </button>
  );
}

function ActionChip({ action }) {
  const isBan = action === 'ban';
  const label = isBan ? t('banHistory.action.ban') : t('banHistory.action.unban');
  const classes = isBan
    ? 'bg-red-500/20 text-red-200 border border-red-400/40'
    : 'bg-green-500/20 text-green-200 border border-green-400/40';
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${classes}`}>
      {label}
    </span>
  );
}

function resolveActionFilter(banOnly, unbanOnly) {
  if (banOnly && !unbanOnly) return 'ban';
  if (unbanOnly && !banOnly) return 'unban';
  return undefined;
}

export default function BanHistoryPage() {
  const navigate = useNavigate();
  const { token, logout } = useAuth();
  const { apiEnvironment } = useApiEnvironment();
  const [page, setPage] = useState(1);
  const [banOnly, setBanOnly] = useState(true);
  const [unbanOnly, setUnbanOnly] = useState(false);
  const [searchDraft, setSearchDraft] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [modalUserId, setModalUserId] = useState(null);

  const actionFilter = resolveActionFilter(banOnly, unbanOnly);

  const logsQuery = useQuery({
    queryKey: ['userBanLogs', apiEnvironment, page, PAGE_SIZE, actionFilter, searchQuery],
    queryFn: () => getUserBanLogs(page, PAGE_SIZE, token, { action: actionFilter, search: searchQuery }),
    enabled: !!token,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (logsQuery.error?.status === 401) {
      logout();
      navigate('/login', { replace: true });
    }
  }, [logsQuery.error, logout, navigate]);

  const {
    items: logs = [],
    total_count: totalCount = 0,
    page: currentPage = 1,
    page_size: pageSize = PAGE_SIZE,
  } = logsQuery.data || {};

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const handleBanOnlyChange = (e) => {
    const checked = e.target.checked;
    setBanOnly(checked);
    if (checked) setUnbanOnly(false);
    setPage(1);
  };

  const handleUnbanOnlyChange = (e) => {
    const checked = e.target.checked;
    setUnbanOnly(checked);
    if (checked) setBanOnly(false);
    setPage(1);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setSearchQuery(searchDraft.trim());
  };

  const handleSearchClear = () => {
    setSearchDraft('');
    setSearchQuery('');
    setPage(1);
  };

  const handleModalUnauthorized = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const rows = useMemo(
    () =>
      logs.map((log) => ({
        id: log.id ?? log.ID,
        userId: log.user_id ?? log.UserID,
        targetUsername: log.target_username ?? log.TargetUsername ?? '',
        action: log.action ?? log.Action,
        adminUserId: log.admin_user_id ?? log.AdminUserID,
        createdAt: log.created_at ?? log.CreatedAt,
      })),
    [logs],
  );

  const emptyLabel = unbanOnly
    ? t('banHistory.table.emptyUnbanOnly')
    : banOnly
      ? t('banHistory.table.emptyBanOnly')
      : t('banHistory.table.empty');

  return (
    <div className="min-h-screen py-8 px-3 sm:px-5 lg:px-6 bg-dark-bg">
      <div className="w-full max-w-[min(100%,1920px)] mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-dark-text">{t('banHistory.title')}</h1>
          <p className="text-dark-text-secondary text-sm mt-1">{t('banHistory.description')}</p>
        </div>

        {logsQuery.isError && (
          <div className="mb-4 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            {logsQuery.error?.message}
          </div>
        )}

        <form onSubmit={handleSearchSubmit} className="mb-4 flex flex-wrap items-center gap-2">
          <input
            type="search"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            placeholder={t('banHistory.search.placeholder')}
            className="flex-1 min-w-[12rem] max-w-md px-3 py-2 rounded-lg bg-dark-card border border-dark-border text-dark-text text-sm placeholder:text-dark-text-muted focus:outline-none focus:border-primary/60"
          />
          <button
            type="submit"
            className="px-4 py-2 rounded-lg text-sm font-medium bg-primary text-black hover:bg-primary/90"
          >
            {t('banHistory.search.submit')}
          </button>
          {searchQuery ? (
            <button
              type="button"
              onClick={handleSearchClear}
              className="px-4 py-2 rounded-lg text-sm border border-dark-border text-dark-text-secondary hover:text-dark-text"
            >
              {t('banHistory.search.clear')}
            </button>
          ) : null}
        </form>

        <div className="mb-4 rounded-xl border border-dark-border bg-dark-card p-4 flex flex-wrap items-center gap-x-6 gap-y-2">
          <label className="text-xs text-dark-text-secondary flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={banOnly}
              disabled={unbanOnly}
              onChange={handleBanOnlyChange}
              className="accent-primary disabled:opacity-40"
            />
            <span>{t('banHistory.filter.banOnly')}</span>
          </label>
          <label className="text-xs text-dark-text-secondary flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={unbanOnly}
              disabled={banOnly}
              onChange={handleUnbanOnlyChange}
              className="accent-primary disabled:opacity-40"
            />
            <span>{t('banHistory.filter.unbanOnly')}</span>
          </label>
        </div>

        <PaginatedTableCard
          error={logsQuery.error}
          dataUpdatedAt={logsQuery.dataUpdatedAt}
          isPending={logsQuery.isPending}
          isFetching={logsQuery.isFetching}
          isEmpty={!logsQuery.isPending && rows.length === 0}
          skeletonRows={PAGE_SIZE}
          skeletonCellWidths={BAN_HISTORY_TABLE_SKELETON_WIDTHS}
          colSpan={6}
          minHeight="min-h-[28.5rem]"
          tableClassName="w-full min-w-[900px]"
          loadingLabel={t('banHistory.table.loading')}
          emptyLabel={emptyLabel}
          page={page}
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={setPage}
          prevLabel={t('banHistory.pagination.prev')}
          nextLabel={t('banHistory.pagination.next')}
          summary={t('banHistory.pagination.page', { page: currentPage, totalPages })}
          header={(
            <tr className="border-b border-dark-border">
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('banHistory.table.id')}</th>
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('banHistory.table.userId')}</th>
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('banHistory.table.username')}</th>
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('banHistory.table.action')}</th>
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('banHistory.table.adminUserId')}</th>
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('banHistory.table.createdAt')}</th>
            </tr>
          )}
        >
          {rows.map((row) => (
            <tr key={row.id} className="border-b border-dark-border last:border-0 hover:bg-dark-surface/50">
              <td className="py-3 px-4 text-dark-text-secondary text-sm">{row.id ?? '-'}</td>
              <td className="py-3 px-4 text-sm">
                <ClickableUserId userId={row.userId} onOpen={setModalUserId} />
              </td>
              <td className="py-3 px-4 text-dark-text text-sm">{row.targetUsername || '-'}</td>
              <td className="py-3 px-4 text-sm align-middle">
                <ActionChip action={row.action} />
              </td>
              <td className="py-3 px-4 text-dark-text-secondary text-sm">{row.adminUserId ?? '-'}</td>
              <td className="py-3 px-4 text-dark-text-secondary text-sm">{formatDateTimeKST(row.createdAt) || '-'}</td>
            </tr>
          ))}
        </PaginatedTableCard>

        {modalUserId != null && token && (
          <UserDetailModal
            userId={modalUserId}
            token={token}
            onClose={() => setModalUserId(null)}
            onUnauthorized={handleModalUnauthorized}
            onNavigateUserId={setModalUserId}
          />
        )}
      </div>
    </div>
  );
}
